import React, { useEffect, useRef, useState } from 'react';
import {
  Alert,
  Avatar,
  Box,
  Chip,
  CircularProgress,
  Divider,
  IconButton,
  Paper,
  Stack,
  TextField,
  Typography,
  alpha,
  useTheme,
} from '@mui/material';
import {
  AutoAwesomeRounded as SparkleIcon,
  CloseRounded as CloseIcon,
  LockOutlined as LockIcon,
  SendRounded as SendIcon,
  SmartToyRounded as BotIcon,
} from '@mui/icons-material';
import { getSuggestedQuestions, sendChatMessage } from '../api/chat';

const MAX_MESSAGE_LENGTH = 2_000;

function timeLabel(value) {
  if (!value) return '';
  return new Date(value).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
}

function MessageText({ children }) {
  return (
    <Typography sx={{ whiteSpace: 'pre-wrap', overflowWrap: 'anywhere', fontSize: '.9rem', lineHeight: 1.7 }}>
      {children}
    </Typography>
  );
}

function Sources({ sources = [] }) {
  if (!sources.length) return null;
  return (
    <Box sx={{ mt: 1.35 }}>
      <Typography sx={{ color: 'text.secondary', fontSize: '.68rem', fontWeight: 800, letterSpacing: '.08em', textTransform: 'uppercase' }}>
        Evidence used
      </Typography>
      <Stack direction="row" flexWrap="wrap" useFlexGap spacing={.75} sx={{ mt: .75 }}>
        {sources.map((item) => (
          <Chip
            key={`${item.type}-${item.id}`}
            size="small"
            label={item.title || item.id}
            title={item.subtitle || item.title || item.id}
            sx={{ maxWidth: '100%', bgcolor: alpha('#0b63ce', .08), color: '#0b4e9c', fontWeight: 700 }}
          />
        ))}
      </Stack>
    </Box>
  );
}

export default function ChatWindow({ open, onClose, trigger }) {
  const theme = useTheme();
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [suggestions, setSuggestions] = useState([]);
  const [error, setError] = useState('');
  const endRef = useRef(null);
  const inputRef = useRef(null);

  useEffect(() => {
    if (!open) return;
    if (!messages.length) {
      setMessages([{
        id: 'welcome',
        role: 'assistant',
        content: 'I am Susha Copilot. I can explain authorised asset data, lifecycle risk, device utilisation and directional cost signals. I only use read-only tools and show the records used in my answer.',
        ts: new Date().toISOString(),
      }]);
    }
    getSuggestedQuestions().then((response) => {
      if (response.success) setSuggestions(response.data || []);
    }).catch(() => {});
  }, [open, messages.length]);

  useEffect(() => {
    if (open) inputRef.current?.focus();
  }, [open, trigger]);

  useEffect(() => {
    endRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages, loading]);

  const ask = async (question = input) => {
    const message = String(question || '').trim();
    if (!message || loading) return;
    if (message.length > MAX_MESSAGE_LENGTH) {
      setError(`Keep your question under ${MAX_MESSAGE_LENGTH} characters.`);
      return;
    }

    const userMessage = { id: `user-${Date.now()}`, role: 'user', content: message, ts: new Date().toISOString() };
    setMessages((current) => [...current, userMessage]);
    setInput('');
    setError('');
    setLoading(true);
    try {
      const response = await sendChatMessage(message);
      if (!response.success) throw new Error('The request could not be completed.');
      setMessages((current) => [...current, {
        id: `assistant-${Date.now()}`,
        role: 'assistant',
        content: response.data.response,
        sources: response.data.sources || [],
        meta: response.data.meta || {},
        ts: new Date().toISOString(),
      }]);
    } catch (requestError) {
      setError(requestError?.response?.data?.message || 'Susha Copilot could not complete that request. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  if (!open) return null;

  return (
    <Paper
      elevation={0}
      role="dialog"
      aria-label="Susha Copilot"
      sx={{
        position: 'fixed', zIndex: 1300, right: { xs: 0, sm: 24 }, bottom: { xs: 0, sm: 24 },
        width: { xs: '100vw', sm: 620, md: 700 }, height: { xs: '100dvh', sm: 'min(760px, calc(100vh - 48px))' },
        display: 'flex', flexDirection: 'column', overflow: 'hidden', borderRadius: { xs: 0, sm: 4 },
        border: `1px solid ${alpha(theme.palette.primary.main, .18)}`,
        boxShadow: '0 28px 80px rgba(12, 31, 54, .30)', bgcolor: '#fff',
      }}
    >
      <Box sx={{ px: { xs: 2, sm: 3 }, py: 2.1, color: '#fff', background: 'linear-gradient(120deg, #092e55, #0b63ce)' }}>
        <Stack direction="row" alignItems="flex-start" justifyContent="space-between" spacing={2}>
          <Stack direction="row" spacing={1.25} alignItems="center">
            <Avatar sx={{ width: 38, height: 38, bgcolor: 'rgba(255,255,255,.16)', border: '1px solid rgba(255,255,255,.25)' }}><BotIcon /></Avatar>
            <Box>
              <Stack direction="row" spacing={.75} alignItems="center">
                <Typography sx={{ fontWeight: 800, fontSize: '1.05rem' }}>Susha Copilot</Typography>
                <Chip size="small" label="Read-only" sx={{ height: 20, bgcolor: 'rgba(255,255,255,.15)', color: '#fff', fontSize: '.62rem', fontWeight: 800 }} />
              </Stack>
              <Typography sx={{ opacity: .78, fontSize: '.72rem', mt: .25 }}>Evidence-based IT asset intelligence</Typography>
            </Box>
          </Stack>
          <IconButton onClick={onClose} aria-label="Close Susha Copilot" sx={{ color: '#fff' }}><CloseIcon /></IconButton>
        </Stack>
      </Box>

      <Alert icon={<LockIcon fontSize="inherit" />} severity="info" sx={{ m: { xs: 1.5, sm: 2 }, mb: 0, py: .2, '& .MuiAlert-message': { fontSize: '.75rem' } }}>
        Answers are scoped to your enterprise and include the records used. Copilot cannot change data.
      </Alert>

      <Box sx={{ flex: 1, minHeight: 0, overflowY: 'auto', px: { xs: 1.5, sm: 2.5 }, py: 2 }}>
        <Stack spacing={2.1}>
          {messages.map((message) => {
            const isAssistant = message.role === 'assistant';
            return (
              <Box key={message.id} sx={{ display: 'flex', justifyContent: isAssistant ? 'flex-start' : 'flex-end', gap: 1 }}>
                {isAssistant && <Avatar sx={{ width: 30, height: 30, mt: .25, bgcolor: alpha(theme.palette.primary.main, .12), color: theme.palette.primary.main }}><SparkleIcon sx={{ fontSize: 17 }} /></Avatar>}
                <Box sx={{ maxWidth: { xs: '88%', sm: '82%' } }}>
                  <Paper sx={{ p: 1.6, borderRadius: 2.5, border: isAssistant ? `1px solid ${alpha(theme.palette.primary.main, .14)}` : 'none', bgcolor: isAssistant ? '#f8fbff' : theme.palette.primary.main, color: isAssistant ? 'text.primary' : '#fff', boxShadow: 'none' }}>
                    <MessageText>{message.content}</MessageText>
                    {isAssistant && <Sources sources={message.sources} />}
                  </Paper>
                  <Typography sx={{ mt: .5, color: 'text.disabled', fontSize: '.65rem', textAlign: isAssistant ? 'left' : 'right' }}>
                    {isAssistant ? 'Susha Copilot' : 'You'} · {timeLabel(message.ts)}
                  </Typography>
                </Box>
              </Box>
            );
          })}
          {loading && <Stack direction="row" spacing={1} alignItems="center" sx={{ color: 'text.secondary' }}><CircularProgress size={17} /><Typography sx={{ fontSize: '.8rem' }}>Reviewing authorised data…</Typography></Stack>}
          <div ref={endRef} />
        </Stack>
      </Box>

      {!!suggestions.length && messages.length <= 1 && (
        <Box sx={{ px: { xs: 1.5, sm: 2.5 }, pb: 1.5 }}>
          <Typography sx={{ color: 'text.secondary', fontSize: '.68rem', fontWeight: 800, letterSpacing: '.08em', textTransform: 'uppercase', mb: .85 }}>Try a question</Typography>
          <Stack direction="row" spacing={.8} flexWrap="wrap" useFlexGap>
            {suggestions.map((suggestion) => <Chip key={suggestion} label={suggestion} onClick={() => ask(suggestion)} disabled={loading} variant="outlined" sx={{ height: 'auto', py: .35, '& .MuiChip-label': { whiteSpace: 'normal', fontSize: '.72rem' } }} />)}
          </Stack>
        </Box>
      )}

      <Divider />
      <Box component="form" onSubmit={(event) => { event.preventDefault(); ask(); }} sx={{ p: { xs: 1.5, sm: 2.25 }, bgcolor: '#fff' }}>
        {error && <Alert severity="error" sx={{ mb: 1.25, '& .MuiAlert-message': { fontSize: '.75rem' } }}>{error}</Alert>}
        <Stack direction="row" spacing={1.2} alignItems="flex-end">
          <TextField inputRef={inputRef} fullWidth multiline maxRows={4} value={input} disabled={loading} onChange={(event) => setInput(event.target.value)} placeholder="Ask about assets, warranty risk, inactive devices or estate posture…" inputProps={{ maxLength: MAX_MESSAGE_LENGTH, 'aria-label': 'Ask Susha Copilot' }} sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2.5, bgcolor: '#f8fafc' } }} />
          <IconButton type="submit" disabled={loading || !input.trim()} aria-label="Send question" sx={{ width: 48, height: 48, borderRadius: 2.5, color: '#fff', bgcolor: theme.palette.primary.main, '&:hover': { bgcolor: theme.palette.primary.dark } }}><SendIcon /></IconButton>
        </Stack>
        <Stack direction="row" justifyContent="space-between" sx={{ mt: .85 }}>
          <Typography sx={{ color: 'text.disabled', fontSize: '.65rem' }}>Session stays in this window; no browser chat history is stored.</Typography>
          <Typography sx={{ color: 'text.disabled', fontSize: '.65rem' }}>{input.length}/{MAX_MESSAGE_LENGTH}</Typography>
        </Stack>
      </Box>
    </Paper>
  );
}
